/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'

import { BP_SMALL_DOWN } from '../../../../lib/styles/breakpoints'
import { hoverColor, secondaryGrayColor } from '../../../../lib/styles/colors'
import { headerCss, pageSubtitleCss } from '../../../../lib/styles/typography'
import useFocus from '../../../../lib/hooks/useFocus'
import TextField from '../TextField'
import { LabelVisibility } from '../../../../types'
import IconButton, { ColorVariant } from '../../buttons/IconButton/IconButton'

type NameFieldProps = {
  /** aria-label to provide more details about the name field */
  ariaLabel?: string
  /** Optional Class Name */
  className?: string
  /** Indicates field is an error state */
  error?: boolean
  /** Default the focus of the field */
  defaultFocused?: boolean
  /** Indicates the field should be focused if there's an error */
  focusOnError?: boolean
  /** Helper text displayed underneath the input */
  helperText?: string
  /** ID of control */
  id: string
  /** Label for control */
  label: string
  /** If multiline, it will render as a textarea */
  multiline?: boolean
  /** Event callback when the input loses focus. */
  onBlur?: () => void
  /** Event callback text has changed. */
  onChange: (newTitle: string) => void
  /** Placeholder text */
  placeholder?: string
  /** Event callback when the input is focused */
  onFocus?: () => void
  /** Number of rows for textarea */
  rows?: number
  /** Whether we should show the edit icon on hover of the name field */
  showEditIcon?: boolean
  /** Value of NameField */
  value?: string
  /** can edit the NameField */
  disabled?: boolean
}

const defaultProps: Omit<NameFieldProps, 'id' | 'label' | 'onChange'> = {
  ariaLabel: undefined,
  className: undefined,
  defaultFocused: false,
  error: false,
  focusOnError: false,
  helperText: undefined,
  multiline: false,
  onBlur: undefined,
  onFocus: undefined,
  placeholder: undefined,
  rows: undefined,
  showEditIcon: false,
  value: undefined,
  disabled: undefined
}

const editButtonCss = css`
  &:hover {
    background-color: unset;
  }
`

export const NameField = ({
  ariaLabel,
  className,
  defaultFocused,
  error,
  focusOnError,
  helperText,
  id,
  label,
  multiline,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  rows,
  showEditIcon,
  value,
  disabled
}: NameFieldProps) => {
  const [isDefaultFocused, setIsDefaultFocused] = useState(defaultFocused)
  const [inputValue, setValue] = useState(value)
  const [focused, setFocused] = useState(!value)
  const [focusOnErr, setFocusOnErr] = useState(focusOnError)
  const [inputRef, setInputFocus] = useFocus()
  /** Keep a reference to the initial value to use for resetting */
  const initialValue = value
  let isEscaping = false

  const transparentCss = css`
    border-radius: 4px;
    margin: 0 0 0 8px;
    padding-bottom: 0 !important;

    &:hover {
      cursor: pointer;
      background-color: ${hoverColor};
    }
    &:focus-within {
      background-color: #ffffff;
      border: 1px solid ${secondaryGrayColor};
      padding-bottom: 3px;
    }

    input,
    textarea {
      ${headerCss};
      background: none;
      border: none;
      line-height: 40px;
      padding: 15px 0 5px 8px;

      @media (${BP_SMALL_DOWN}) {
        ${pageSubtitleCss};
      }

      &:hover {
        cursor: pointer;
      }
    }

    input ~ .border,
    textarea ~ .border {
      padding-right: ${showEditIcon ? 0 : '10px'};
      bottom: 0;
    }

    input {
      height: 1.1875em;
    }

    // unique font
    textarea {
      font-weight: 500;
      font-size: 20px;
      line-height: 22px;
      overflow: hidden;
      resize: none;
    }
  `

  // use default focus mainly for storybook purposes
  useEffect(() => {
    if (isDefaultFocused) {
      setInputFocus()
      setIsDefaultFocused(false)
    }
  }, [isDefaultFocused, setInputFocus])

  //  For accessibility purposes, focus on the title if there's an error
  useEffect(() => {
    if (inputRef && error && focusOnErr) {
      setInputFocus()
      setFocusOnErr(false)
    }
  }, [inputRef, setInputFocus, error, focusOnErr])

  const handleEditClick = () => {
    setInputFocus()
    handleFocus()
  }

  const handleFocus = () => {
    setFocused(true)

    if (onFocus) {
      onFocus()
    }
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      setValue(initialValue)
    }
    // So this isn't called on every keystroke, use onBlur instead.
    // Don't call the onChange until focus changes or we'll end up
    // with a value for every keystroke in the change log.
    else if (inputValue !== initialValue && !isEscaping) {
      onChange(event.target.value)
    }
    isEscaping = false
    setFocused(false)

    if (onBlur) {
      onBlur()
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement

    // Reset the value to its original state on escape
    if (event.key === 'Escape') {
      setValue(initialValue)
      isEscaping = true

      // Move focus out of the input
      target.blur()
    }

    // Trigger the change when the enter key is hit
    if (event.key === 'Enter') {
      target.blur()
    }
  }

  const labelVisibility = focused ? LabelVisibility.DEFAULT : LabelVisibility.HIDDEN

  return (
    <TextField
      id={id}
      ariaLabel={ariaLabel}
      className={className}
      css={transparentCss}
      endAdornment={
        showEditIcon ? (
          <IconButton
            ariaLabel={`Edit ${ariaLabel}`}
            colorVariant={ColorVariant.Primary}
            css={editButtonCss}
            displayFocusRipple={false}
            displayTooltip={false}
            onClick={handleEditClick}
            disabled={disabled}
          >
            <EditIcon />
          </IconButton>
        ) : undefined
      }
      error={error}
      helperText={helperText}
      inputRef={inputRef}
      label={label}
      labelVisibility={labelVisibility}
      maxLength={70}
      multiline={multiline}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      rows={rows}
      value={inputValue}
      disabled={disabled}
    />
  )
}

NameField.defaultProps = defaultProps

export default NameField
